import React, { useRef, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useStaticQuery, graphql } from 'gatsby'
import Img, { FluidObject } from 'gatsby-image'

import { Container } from 'components/Container'
import { backgroundColors, colors } from 'styles/colors'
import { displayWidth } from 'styles/width'
import { mobileAfterBorder } from 'styles/mobileAfterBorder'
import { Title } from 'components/TitleComponent'
import { getDataByLanguage } from 'utils/getDataByLanguage'
import { SliderComponent } from 'components/SliderComponent'
import { indent } from 'styles/indent'
import FullScreen from 'assets/icons/fullScreen.svg'
import { ModalCarousel } from 'components/ModalCarousel'
import { ProjectData } from 'layout/Project'
import { sendEvent } from 'tracking'
import { headerBg } from 'styles/headerBg'
import { LeftSidebar, RightSidebar } from 'components/SideBar'
import Slider from 'react-slick'

const ExampleOfProjectWrapper = styled.div`
    display: flex;
    justify-content: center;
    width: 100%;
    background-color: ${backgroundColors.promotion};
    position: relative;
    border-bottom: 1px solid ${colors.dark};
    ${mobileAfterBorder}
    :before {
        ${headerBg}
    }
`

const TitleStyled = styled(Title)`
    font-size: 24px;
    @media (min-width: ${displayWidth.tablet}) {
        margin-left: 0;
        margin-right: 0;
    }
`
const HeroColumn = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0 ${indent.heroColumnTablet} 24px;
    border-bottom: 1px solid ${colors.dark};
    @media (min-width: ${displayWidth.tablet}) {
        border-bottom: none;
        border-right: 1px solid ${colors.dark};
    }
    @media (min-width: ${displayWidth.desktop}) {
        padding: 0 ${indent.heroColumnDesktop} 24px;
    }
`

const ImgStyled = styled(Img)<{ fluid: FluidObject }>`
    width: 100%;
    height: 100%;
`

const Wrapper = styled.div`
    display: flex;
    align-items: center;
    background-color: ${colors.white};
    position: relative;
`

const ImgName = styled.p`
    margin: 4px 20px;
    font-size: 20px;
    @media (min-width: ${displayWidth.tablet}) {
        margin: 4px 0;
    }
`

const WrapperMobile = styled(Wrapper)`
    display: flex;
    flex-direction: column;
    @media (min-width: ${displayWidth.tablet}) {
        display: none;
    }
`
const WrapperDesktop = styled(Wrapper)`
    display: none;
    @media (min-width: ${displayWidth.tablet}) {
        display: flex;
        flex-direction: column;
    }
`
const FullScreenButton = styled(FullScreen)`
    fill: ${colors.white};
    stroke: ${colors.dark};
    stroke-width: 1px;
    position: absolute;
    top: 10px;
    right: 26px;
    width: 50px;
    height: 50px;
    cursor: pointer;
    z-index: 3;
    display: none;
    @media (min-width: ${displayWidth.tablet}) {
        display: block;
    }
    @media (min-width: ${displayWidth.desktop}) {
        right: 10px;
    }
`

export const StylesSliderBlock = () => {
    const { i18n, t } = useTranslation()
    const [isModalOpen, setModalIsOpen] = useState(false)
    const sliderRefD = useRef<Slider | null>(null)
    const sliderRefM = useRef<Slider | null>(null)
    const [currentSlideS, setCurrentSlideS] = useState(0)
    const data = useStaticQuery(graphql`
        query {
            desktop: allFile(
                filter: { relativeDirectory: { eq: "styles" } }
                sort: { fields: absolutePath }
            ) {
                edges {
                    node {
                        id
                        childImageSharp {
                            fluid(srcSetBreakpoints: [800], quality: 100) {
                                ...GatsbyImageSharpFluid
                            }
                            parent {
                                ... on File {
                                    name
                                }
                            }
                        }
                    }
                }
            }
            allStylesImageDataYaml {
                edges {
                    node {
                        items {
                            title
                            alt
                        }
                        parent {
                            ... on File {
                                name
                            }
                        }
                    }
                }
            }
        }
    `)

    const imagesData = getDataByLanguage(
        data.allStylesImageDataYaml,
        i18n.language
    )

    const sliderSettings = {
        infinite: true,
        responsive: [
            {
                breakpoint: 1024,
                settings: {
                    arrows: true,
                    dots: true,
                },
            },
        ],
    }
    const filesDesktop = data.desktop.edges

    return (
        <ExampleOfProjectWrapper>
            <LeftSidebar background={backgroundColors.promotion} />
            <Container columns={'1fr'} tabletColumns={'1fr 2fr'}>
                <HeroColumn>
                    <TitleStyled>{t('styleSelectionTitleH2')}</TitleStyled>
                </HeroColumn>
                <WrapperDesktop>
                    <FullScreenButton
                        onClick={() => {
                            sendEvent('FullScreen', {
                                eventCategory: 'SliderExample',
                                placement: 'ExampleOfProject',
                            })
                            setModalIsOpen(true)
                        }}
                    />
                    <SliderComponent
                        forwardRef={sliderRefD}
                        {...sliderSettings}
                        afterChange={(current: number) => {
                            setCurrentSlideS(current)
                            sendEvent('ShowSlide', {
                                eventCategory: 'SliderExample',
                                currentSlide: `${current}`,
                                placement: 'ExampleOfProject',
                            })
                        }}
                    >
                        {filesDesktop.map(
                            (
                                item: {
                                    node: {
                                        childImageSharp: {
                                            fluid: FluidObject
                                            parent: { name: string }
                                        }
                                    }
                                },
                                index: number
                            ) => {
                                return (
                                    <ImgStyled
                                        key={index}
                                        fluid={item.node.childImageSharp.fluid}
                                        imgStyle={{
                                            objectFit: 'containe',
                                        }}
                                        alt={imagesData.items[index]?.alt}
                                        title={imagesData.items[index]?.title}
                                    />
                                )
                            }
                        )}
                    </SliderComponent>
                    <ImgName>{imagesData.items[currentSlideS]?.alt}</ImgName>
                </WrapperDesktop>
                <WrapperMobile>
                    <SliderComponent
                        forwardRef={sliderRefM}
                        {...sliderSettings}
                        afterChange={(current: number) => {
                            setCurrentSlideS(current)
                            sendEvent('ShowSlide', {
                                eventCategory: 'SliderExample',
                                currentSlide: `${current + 1}`,
                                placement: 'ExampleOfProject',
                            })
                        }}
                    >
                        {filesDesktop.map(
                            (
                                item: {
                                    node: {
                                        childImageSharp: {
                                            fluid: FluidObject
                                            parent: { name: string }
                                        }
                                    }
                                },
                                index: number
                            ) => {
                                return (
                                    <ImgStyled
                                        key={index}
                                        fluid={item.node.childImageSharp.fluid}
                                        imgStyle={{
                                            objectFit: 'containe',
                                        }}
                                        alt={imagesData.items[index]?.alt}
                                        title={imagesData.items[index]?.title}
                                    />
                                )
                            }
                        )}
                    </SliderComponent>
                    <ImgName>{imagesData.items[currentSlideS]?.alt}</ImgName>
                </WrapperMobile>
            </Container>
            <RightSidebar />
            <ModalCarousel
                data={filesDesktop.map(
                    ({ node }: { node: ProjectData }) => node
                )}
                isModalOpen={isModalOpen}
                closeHandler={() => setModalIsOpen(false)}
                initialSlideIndex={0}
            />
        </ExampleOfProjectWrapper>
    )
}
